import { Stack, Typography, Box, MenuItem, Select } from "@mui/material";
import type { ComponentType, FC, ReactNode } from "react";
import { getGridDateOperators, getGridStringOperators } from "@mui/x-data-grid-premium";
import type { GridColDef, GridFilterInputValueProps, GridFilterOperator } from "@mui/x-data-grid-premium";
import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import { logError, logUnhandled } from "../api/logError";
import type { GridViewAgreement, System } from "../api/systems.types";
import type { User, UsersByOrganisationId } from "../api/users.types";
import type {
  SingleSelectInput,
  QuestionCard,
  CountryInput,
  InputType,
  Answer,
  Option,
  MultipleAgreementValue,
  MultiplePartnerValue,
} from "../dynamicForm";
import type { IconProps } from "../shared/icons";
import { SystemIcon3, PersonIcon3, DpiaIcon3, MeasureIcon3, PhoneIcon, MailIcon, FileTextIcon } from "../shared/icons";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { DynamicForm } from "../dynamicForm";
import { HoverPopover, useHoverPopover } from "../shared/HoverPopover";
import { DynamicShared } from "../dynamicShared";
import { DocumentPreview, formatDocumentReference } from "../dynamicShared/DocumentLink";
import { getDocumentReferenceKey, toFileReference } from "../dynamicForm/types";
import { multilineEllipsis } from "../shared/multilineEllipsis";
import type { DpiasResponse } from "../api/dpia.types";
import { flattenTenantHierarchy } from "../shared/TenantSelect";
import type { Measure } from "../api/measures.types";
import type { GetTenantHierarchyListResponse } from "../api/tenants";
import type { PersonalDataControllerListItem } from "../api/personalDataControllers";
import { Flag } from "../shared/Flag";
import { ContactPersonContent } from "../shared/ContactPerson";
import type { GetSelectPartnerOptionsResponse } from "../api/partners";
import type { EnterpriseCustomerOrganisation } from "../api/enterpriseCustomer";
import { getPartnerInfo } from "../dynamicForm/shared/MultiplePartnerInputBase";
import { getPartnerIds, SUPPLIER_QUESTION_ID } from "../agreements/supplierQuestions";
import { Grid } from "../shared/Grid";
import { getLabelFromOptions, requiredColDef } from "./misc";
import { ColumnHeader } from "./ColumnHeader";
import { getDateSortComparator } from "./dateSortComparator";
import { getDateQuickFilter } from "./dateQuickFilter";
import { hasOptionFilterOperator } from "./hasOptionFilter";
import { HACK_alignIconWithLabel } from "@privacy/ui";

/** undefined is treated as "loading". And, missing properties are treated as "not used". */
export type AsyncQuestionCardData = Partial<{
  systemsQueryData: System[];
  usersQueryData: UsersByOrganisationId;
  dpiasQueryData: DpiasResponse;
  multiTenantsQueryData: GetTenantHierarchyListResponse;
  personalDataControllersQueryData: PersonalDataControllerListItem[];
  measuresQueryData: Measure[];
  partnersQueryData: GetSelectPartnerOptionsResponse;
  enterpriseCustomerOrganisationsQueryData: Array<Pick<EnterpriseCustomerOrganisation, "id" | "organizationName">>;
  agreementsQueryData: GridViewAgreement[];
}>;

type GetQuestionCardToColDef = (args: {
  questionCard: QuestionCard;
  asyncData: AsyncQuestionCardData;
  t: (translation: TranslationKey) => string;
  formatDate: (date: string) => string;
}) => GridColDef | null;

export const getQuestionCardToColDef: GetQuestionCardToColDef = ({ questionCard, asyncData, t, formatDate }) => {
  switch (questionCard.input.type) {
    case "textInput":
      return getTextInputColDef()(questionCard);
    case "dateInput":
      return getDateInputColDef(formatDate)(questionCard);
    case "singleChoiceInput":
      return getSingleChoiceInputColDef(questionCard.input)(questionCard);
    case "multipleChoiceInput":
      return getMultiChoiceInputColDef(questionCard.input.options)(questionCard);
    case "dynamicMultipleChoiceInput":
      return getDynamicMultipleChoiceColDef(t)(questionCard);
    case "countryInput":
      return getCountryInputColDef(questionCard.input)(questionCard);
    case "multipleExternalLinkInput":
      return getMultipleExternalLinkInputColDef()(questionCard);
    case "externalLinkInput":
      return getExternalLinkInputColDef()(questionCard);
    case "emailInput":
      return getEmailInputColDef()(questionCard);
    case "phoneNumberInput":
      return getPhoneNumberInputColDef()(questionCard);
    case "yesOrNoInput":
      return getYesOrNoInputColDef(t)(questionCard);
    case "multipleContactPersonInput":
      return getMultipleContactPersonInputColDef()(questionCard);
    case "multipleTenantInput":
      return "multiTenantsQueryData" in asyncData
        ? getTenantInputColDef(asyncData.multiTenantsQueryData)(questionCard)
        : handleNoAsyncData("multipleTenantInput");
    case "systemInput":
      return "systemsQueryData" in asyncData
        ? getSystemInputColDef(asyncData.systemsQueryData)(questionCard)
        : handleNoAsyncData("systemInput");
    case "measureInput":
      return "measuresQueryData" in asyncData
        ? getMeasureInputColDef(asyncData.measuresQueryData)(questionCard)
        : handleNoAsyncData("measureInput");
    case "personInput":
      return "usersQueryData" in asyncData
        ? getPersonInputColDef(asyncData.usersQueryData)(questionCard)
        : handleNoAsyncData("personInput");
    case "singleDpiaInput":
      return "dpiasQueryData" in asyncData
        ? getDpiaInputColDef(asyncData.dpiasQueryData)(questionCard)
        : handleNoAsyncData("singleDpiaInput");
    case "multiplePersonInput":
      return "usersQueryData" in asyncData
        ? getPersonMultipleInputColDef(asyncData.usersQueryData)(questionCard)
        : handleNoAsyncData("multiplePersonInput");
    case "multiplePartnerInput":
      return "partnersQueryData" in asyncData && "personalDataControllersQueryData" in asyncData
        ? getMultiplePartnerInputColDef(
            asyncData.partnersQueryData,
            asyncData.personalDataControllersQueryData,
            asyncData.enterpriseCustomerOrganisationsQueryData
          )(questionCard)
        : handleNoAsyncData("multiplePartnerInput");
    case "multipleSubSupplierInput":
      logError(
        new Error(
          "multipleSubSupplierInput is not implemented for the grid. If you see this error it is time to implement it."
        )
      );
      return null;
    case "multipleAgreementInput":
      return "agreementsQueryData" in asyncData
        ? getMultipleAgreementInputColDef(asyncData.agreementsQueryData)(questionCard)
        : null;
    default:
      return logUnhandled(questionCard.input, "input");
  }
};

const handleNoAsyncData = (inputType: QuestionCard["input"]["type"]) => {
  logError(
    `No async data was provided for input type: "${inputType}". If the consumer grid has question of that type, make sure to fetch and pass the required data.`
  );
  return null;
};

type GridColDefWithAnswerValue<T extends InputType = InputType> = GridColDef<{
  [questionId: string]: Extract<Answer, { type: T }>["value"] | undefined;
}>;

const getTextInputColDef =
  () =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"textInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 350,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => row[id],
      renderCell: ({ row }) => <TextInputCell value={row[id]} />,
    };
  };

export const TextInputCell = ({ value }: { value?: string | null }) => {
  const { popoverProps, anchorProps } = useHoverPopover();
  if (!value) return <EmptyCell />;
  return (
    <Cell withExtraSpacingTop={false}>
      <HoverPopover {...popoverProps}>
        <Stack spacing={1} sx={{ px: 3, py: 2, maxWidth: "20rem" }}>
          <Typography
            variant="body2"
            sx={{ fontSize: theme => theme.typography.pxToRem(12), textWrap: "balance", whiteSpace: "pre-wrap" }}
          >
            {value}
          </Typography>
        </Stack>
      </HoverPopover>
      <Box sx={multilineEllipsis(2)} component="span" {...anchorProps}>
        {value}
      </Box>
    </Cell>
  );
};

const getDateInputColDef =
  (formatDate: (date: string) => string) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"textInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ headerName: header, field: id }),
      filterOperators: [...getGridDateOperators()],
      width: 220,
      getSortComparator: getDateSortComparator,
      getApplyQuickFilterFn: getDateQuickFilter(formatDate),
      valueGetter: (_, row) => (row[id] ? new Date(row[id]) : null),
      renderCell: ({ row }) => (
        <Cell>
          <DateCell value={row[id]} />
        </Cell>
      ),
    };
  };

const DateCell = ({ value }: { value?: string | null }) => {
  const { formatDate } = useTranslation();
  if (!value) return <EmptyCell />;
  return <TextCellContent value={formatDate(value, {})} />;
};

const getYesOrNoInputColDef =
  (t: (translation: TranslationKey) => string) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"yesOrNoInput"> => {
    const { id, header } = questionCard;

    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 250,
      filterOperators: [yesOrNoFilterOperator],
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const value = row[id];
        if (!value) return null;
        return DynamicForm.isYesOrNoValue(value) ? t(DynamicForm.yesOrNoInfo[value].labelKey) : "";
      },
      renderCell: ({ row }) => {
        const value = row[id];
        if (!value) return <EmptyCell />;

        return (
          <Cell withExtraSpacingTop={false}>
            <DynamicForm.YesOrNoInputSelectedValue value={value} />
          </Cell>
        );
      },
    };
  };

const YesOrNotFilterInput = (props: GridFilterInputValueProps) => {
  const { t } = useTranslation();
  const { item, applyValue, focusElementRef } = props;

  return (
    <Select
      fullWidth
      size="small"
      value={item.value ?? ""}
      renderValue={selectedLabel => {
        if (!selectedLabel) return <EmptyCell />;

        const value = t(DynamicForm.yesOrNoInfo["yes"].labelKey) === selectedLabel ? "yes" : "no";

        return (
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            {DynamicForm.yesOrNoInfo[value].icon}
            {t(DynamicForm.yesOrNoInfo[value].labelKey)}
          </Stack>
        );
      }}
      onChange={event => applyValue({ ...item, value: event.target.value })}
      inputRef={focusElementRef}
    >
      <MenuItem hidden sx={{ display: "none" }} value="">
        {t("Filter.DefaultValue")}
      </MenuItem>
      <MenuItem value={t(DynamicForm.yesOrNoInfo["yes"].labelKey)}>
        {DynamicForm.yesOrNoInfo["yes"].icon}
        {t(DynamicForm.yesOrNoInfo["yes"].labelKey)}
      </MenuItem>
      <MenuItem value={t(DynamicForm.yesOrNoInfo["no"].labelKey)}>
        {DynamicForm.yesOrNoInfo["no"].icon}
        {t(DynamicForm.yesOrNoInfo["no"].labelKey)}
      </MenuItem>
    </Select>
  );
};

export const yesOrNoFilterOperator: GridFilterOperator = {
  value: "is",
  getApplyFilterFn: filterItem => {
    if (!filterItem.field || !filterItem.value || !filterItem.operator) return null;
    return value => value === filterItem.value;
  },
  InputComponent: YesOrNotFilterInput,
};

const getMultiChoiceInputColDef =
  (options: Option[]) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"multipleChoiceInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 420,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const values = row[id];
        if (!values) return null;
        return values.map(v => getLabelFromOptions(v, options)).join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const values = row[id];
        if (!values) return <EmptyCell />;
        const labels = values.map(v => getLabelFromOptions(v, options));

        if (labels.length === 0) return <EmptyCell />;

        return (
          <Cell withExtraSpacingTop={false}>
            <LimitVisibleItems
              renderItem={label => label && <DynamicShared.Chip variant="choice" label={label} key={label} />}
              numberOfVisibleItems={6}
              items={labels}
            />
          </Cell>
        );
      },
    };
  };

const getDynamicMultipleChoiceColDef =
  (t: (translation: TranslationKey) => string) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"dynamicMultipleChoiceInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 420,
      filterOperators: [...getGridStringOperators(), hasOptionFilterOperator(t("DynamicFormGrid.Filter.HasOption"))],
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const labelsAndValues = row[id];
        if (!labelsAndValues) return null;

        return labelsAndValues.map(({ label }) => label).join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const labelsAndValues = row[id];
        if (!labelsAndValues || labelsAndValues.length === 0) return <EmptyCell />;

        return (
          <Cell withExtraSpacingTop={false}>
            <LimitVisibleItems
              renderItem={label => <DynamicShared.Chip variant="choice" label={label} key={label} />}
              numberOfVisibleItems={8}
              items={labelsAndValues.map(({ label }) => label)}
            />
          </Cell>
        );
      },
    };
  };

const getCountryInputColDef =
  (input: CountryInput) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"countryInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 250,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const value = row[id];
        if (!value) return null;
        const values = Array.isArray(value) ? value : [value];
        return values.map(v => getLabelFromOptions(v, input.options)).join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const value = row[id];
        if (!value) return <EmptyCell />;
        const values = Array.isArray(value) ? value : [value];
        const valuesAndLabels = values.map(value => ({ value, label: getLabelFromOptions(value, input.options) }));

        if (valuesAndLabels.length === 0) return <EmptyCell />;

        return (
          <Cell>
            <LimitVisibleItems
              renderItem={({ label, value }) => (
                <Stack direction="row" key={value} sx={{ mr: 1, alignItems: "center", gap: 1 }}>
                  <Flag countryCode={value} />
                  <Typography variant="body2">{label}</Typography>
                </Stack>
              )}
              numberOfVisibleItems={2}
              items={valuesAndLabels}
            />
          </Cell>
        );
      },
    };
  };

const getSingleChoiceInputColDef =
  (input: SingleSelectInput) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"singleChoiceInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 260,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => getLabelFromOptions(row[id], input.options),
      renderCell: ({ value }) => {
        if (!value) return <EmptyCell />;
        return (
          <Cell withExtraSpacingTop={false}>
            <DynamicShared.Chip label={value} variant="choice" />
          </Cell>
        );
      },
    };
  };

const getSystemInputColDef =
  (systemsQueryData: AsyncQuestionCardData["systemsQueryData"]) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"systemInput"> => {
    const systemIdsToSystems = (systemsId: string[]) =>
      systemsQueryData
        ? systemsId
            .map(systemId => systemsQueryData.find(system => systemId === system.id))
            .filter(system => system !== undefined)
        : [];

    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 320,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const selectedSystemIds = row[id];
        if (!Array.isArray(selectedSystemIds)) return null;
        return systemIdsToSystems(selectedSystemIds)
          .map(system => system.name)
          .join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const selectedSystemIds = row[id];
        if (!Array.isArray(selectedSystemIds)) return <EmptyCell />;

        return (
          <Cell>
            <SystemsCell systems={systemIdsToSystems(selectedSystemIds)} />
          </Cell>
        );
      },
    };
  };

const SystemsCell: FC<{ systems: System[] }> = ({ systems }) => {
  const { paths } = usePrivacyNavigate();

  if (systems.length === 0) return <EmptyCell />;

  return (
    <LimitVisibleItems
      items={systems}
      numberOfVisibleItems={6}
      renderItem={system => (
        <DynamicShared.Link sx={{ mr: 1 }} Icon={SystemIcon3} key={system.id} to={paths.systemsInline(system.id)}>
          {system.name}
        </DynamicShared.Link>
      )}
    />
  );
};

const getTenantInputColDef =
  (tenantsQueryData: AsyncQuestionCardData["multiTenantsQueryData"]) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"multipleTenantInput"> => {
    const flatten = tenantsQueryData ? flattenTenantHierarchy(tenantsQueryData) : [];

    const tenantIdsToTenants = (tenantsId: string[]) =>
      tenantsQueryData
        ? tenantsId
            .map(tenantId => flatten.find(tenant => tenantId === tenant.id))
            .filter(tenant => tenant !== undefined)
        : [];

    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 320,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const selectedTenantsIds = row[id];
        if (!Array.isArray(selectedTenantsIds)) return null;
        return tenantIdsToTenants(selectedTenantsIds)
          .map(tenant => tenant?.organizationName)
          .join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const selectedTenantsIds = row[id];
        if (!Array.isArray(selectedTenantsIds)) return <EmptyCell />;
        const tenants = tenantIdsToTenants(selectedTenantsIds);

        if (tenants.length === 0) return <EmptyCell />;
        return (
          <Cell withExtraSpacingTop={false}>
            <LimitVisibleItems
              items={tenants}
              numberOfVisibleItems={6}
              renderItem={tenant => (
                <DynamicShared.Chip key={tenant?.id} label={tenant?.organizationName ?? ""} variant="choice" />
              )}
            />
          </Cell>
        );
      },
    };
  };

const getMeasureInputColDef =
  (measuresQueryData: AsyncQuestionCardData["measuresQueryData"]) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"measureInput"> => {
    const { id, header } = questionCard;
    const measureIdsToMeasures = (measureIds: string[]) =>
      measuresQueryData ? measureIds.map(measureId => measuresQueryData.find(measure => measureId === measure.id)) : [];

    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 420,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const selectedMeasureIds = row[id];
        if (!Array.isArray(selectedMeasureIds)) return null;
        return measureIdsToMeasures(selectedMeasureIds)
          .map(measure => measure?.name)
          .join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const value = row[id];

        if (!Array.isArray(value)) return <EmptyCell />;

        const measures = measureIdsToMeasures(value).filter(measure => measure !== undefined);
        return (
          <Cell>
            <MeasuresCell measures={measures} />
          </Cell>
        );
      },
    };
  };

const MeasuresCell: FC<{ measures: Measure[] }> = ({ measures }) => {
  const { paths } = usePrivacyNavigate();

  if (measures.length === 0) return <EmptyCell />;

  return (
    <LimitVisibleItems
      items={measures}
      numberOfVisibleItems={6}
      renderItem={measure => (
        <DynamicShared.Link sx={{ mr: 1 }} Icon={MeasureIcon3} key={measure.id} to={paths.measuresInline(measure.id)}>
          {measure.name}
        </DynamicShared.Link>
      )}
    />
  );
};

const getPersonMultipleInputColDef =
  (usersQueryData: AsyncQuestionCardData["usersQueryData"]) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"multiplePersonInput"> => {
    const userIdsToUsers = (usersId: string[]) =>
      usersQueryData
        ? usersId
            .map(userId => usersQueryData.users.find(user => userId === user.userId))
            .filter(user => user !== undefined)
        : [];

    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 260,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const selectUserIds = row[id];
        if (!Array.isArray(selectUserIds)) return null;
        return userIdsToUsers(selectUserIds)
          .map(user => `${user.firstName} ${user.lastName}`)
          .join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const selectUserIds = row[id];
        if (!Array.isArray(selectUserIds)) return <EmptyCell />;
        return (
          <Cell withExtraSpacingTop={false}>
            <PersonCell users={userIdsToUsers(selectUserIds)} />
          </Cell>
        );
      },
    };
  };

export const PersonCell: FC<{ users: User[] }> = ({ users }) => {
  if (users.length === 0) return <EmptyCell />;

  return (
    <LimitVisibleItems
      items={users}
      numberOfVisibleItems={2}
      renderItem={user => (
        <TextCellContent Icon={PersonIcon3} value={`${user.firstName} ${user.lastName}`} key={user.userId} />
      )}
    />
  );
};

const getPersonInputColDef =
  (usersQueryData: AsyncQuestionCardData["usersQueryData"]) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"personInput"> => {
    const personIdToPerson = (personId: string) =>
      usersQueryData ? usersQueryData.users.find(person => person.userId === personId) : null;

    const { id, header } = questionCard;

    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 260,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const selectedPersonId = row[id];
        if (!selectedPersonId) return null;
        const person = personIdToPerson(selectedPersonId);
        return person ? `${person.firstName} ${person.lastName}` : null;
      },
      renderCell: ({ row }) => {
        const selectedPersonId = row[id];
        if (!selectedPersonId) return null;

        const person = personIdToPerson(selectedPersonId);

        if (!person) return <EmptyCell />;

        return (
          <Cell withExtraSpacingTop={false}>
            <TextCellContent Icon={PersonIcon3} value={`${person.firstName} ${person.lastName}`} />
          </Cell>
        );
      },
    };
  };

const getDpiaInputColDef =
  (dpiasQueryData: AsyncQuestionCardData["dpiasQueryData"]) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"singleDpiaInput"> => {
    const dpiaIdToDpia = (dpiaId: string) => (dpiasQueryData ? dpiasQueryData.find(({ id }) => id === dpiaId) : null);

    const { id, header } = questionCard;

    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 260,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const selectedDpiaId = row[id];
        if (!selectedDpiaId) return null;
        const dpia = dpiaIdToDpia(selectedDpiaId);
        return dpia ? `${dpia.name}` : null;
      },
      renderCell: ({ row }) => {
        const selectedDpiaId = row[id];
        if (!selectedDpiaId) return <EmptyCell />;

        const dpia = dpiaIdToDpia(selectedDpiaId);
        if (!dpia) return <EmptyCell />;

        return (
          <Cell>
            <DpiaCell dpia={dpia} />
          </Cell>
        );
      },
    };
  };

const DpiaCell: FC<{ dpia: { name: string; id: string } }> = ({ dpia }) => {
  const { paths } = usePrivacyNavigate();
  return (
    <DynamicShared.Link Icon={DpiaIcon3} key={dpia.id} to={paths.dpiaById(dpia.id)}>
      {dpia.name}
    </DynamicShared.Link>
  );
};

const getMultipleExternalLinkInputColDef =
  () =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"multipleExternalLinkInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 350,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => row[id]?.map(formatDocumentReference).join(Grid.VALUE_GETTER_DELIMITER),
      renderCell: ({ row }) => {
        const value = row[id];
        if (!value?.length) return <EmptyCell />;

        return (
          <Box sx={{ minWidth: 0 }}>
            <LimitVisibleItems
              items={value}
              numberOfVisibleItems={2}
              stacked
              renderItem={reference => (
                <DocumentPreview key={getDocumentReferenceKey(reference)} reference={reference} />
              )}
            />
          </Box>
        );
      },
    };
  };

const getMultiplePartnerInputColDef =
  (
    partnersQueryData: AsyncQuestionCardData["partnersQueryData"],
    personalDataControllersQueryData: AsyncQuestionCardData["personalDataControllersQueryData"],
    enterpriseCustomerOrganisationsQueryData: AsyncQuestionCardData["enterpriseCustomerOrganisationsQueryData"]
  ) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"multiplePartnerInput"> => {
    const { id, header } = questionCard;

    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 320,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const entities = row[id];
        if (!entities || !partnersQueryData || !personalDataControllersQueryData) return null;
        return entities
          .map(
            entity =>
              getPartnerInfo(entity, {
                selectPartnerOptions: partnersQueryData,
                personalDataControllers: personalDataControllersQueryData,
                enterpriseCustomerOrganisations: enterpriseCustomerOrganisationsQueryData,
              })?.name
          )
          .filter(Boolean)
          .join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const entities = row[id];
        if (!entities || !partnersQueryData || !personalDataControllersQueryData) return null;

        return (
          <Cell withExtraSpacingTop={false}>
            <LimitVisibleItems
              renderItem={entity => {
                const partnerInfo = getPartnerInfo(entity, {
                  selectPartnerOptions: partnersQueryData,
                  personalDataControllers: personalDataControllersQueryData,
                  enterpriseCustomerOrganisations: enterpriseCustomerOrganisationsQueryData,
                });
                if (!partnerInfo) return null;

                return <TextCellContent key={entity.id} Icon={partnerInfo.Icon} value={partnerInfo.name} />;
              }}
              numberOfVisibleItems={3}
              items={entities}
            />
          </Cell>
        );
      },
    };
  };

const getMultipleAgreementInputColDef =
  (agreementsQueryData: AsyncQuestionCardData["agreementsQueryData"]) =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"multipleAgreementInput"> => {
    const { id, header } = questionCard;

    const getAgreementLabels = (row: Record<string, MultipleAgreementValue | null | undefined>) => {
      const value = row[id];
      if (!value || !agreementsQueryData) return [];
      const suppliers = row[SUPPLIER_QUESTION_ID] as MultiplePartnerValue | null | undefined;
      const showPartnerName = getPartnerIds(suppliers).length > 1;
      return value.flatMap(({ agreementId }) => {
        const agreement = agreementsQueryData.find(candidate => candidate.id === agreementId);
        if (!agreement) return [];
        return [
          { agreement, label: showPartnerName ? `${agreement.title} (${agreement.partnerName})` : agreement.title },
        ];
      });
    };

    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 420,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) =>
        getAgreementLabels(row)
          .map(({ label }) => label)
          .join(Grid.VALUE_GETTER_DELIMITER),
      renderCell: ({ row }) => {
        const agreementLabels = getAgreementLabels(row);
        if (agreementLabels.length === 0) return <EmptyCell />;

        return (
          <Cell>
            <LimitVisibleItems
              items={agreementLabels}
              numberOfVisibleItems={3}
              stacked
              renderItem={({ agreement, label }) =>
                agreement.kind === "file" ? (
                  <DocumentPreview key={agreement.id} reference={toFileReference(agreement, label)} />
                ) : agreement.url ? (
                  <DynamicShared.ExternalLink
                    key={agreement.id}
                    Icon={FileTextIcon}
                    title={agreement.url}
                    href={agreement.url}
                  >
                    {label}
                  </DynamicShared.ExternalLink>
                ) : (
                  <Typography key={agreement.id}>{label}</Typography>
                )
              }
            />
          </Cell>
        );
      },
    };
  };

const getExternalLinkInputColDef =
  () =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"externalLinkInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 350,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => row[id],
      renderCell: ({ row }) => {
        const value = row[id];
        if (!value) return <EmptyCell />;

        return (
          <Cell>
            <DynamicShared.ExternalLink href={value}>{value}</DynamicShared.ExternalLink>
          </Cell>
        );
      },
    };
  };
const getEmailInputColDef =
  () =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"emailInput"> => {
    const { id, header } = questionCard;

    return {
      ...requiredColDef({ field: id, headerName: header }),
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => row[id],
      renderCell: ({ row }) => (
        <Cell withExtraSpacingTop={false}>
          <TextCellContent Icon={MailIcon} value={row[id]} />
        </Cell>
      ),
    };
  };

const getPhoneNumberInputColDef =
  () =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"phoneNumberInput"> => {
    const { id, header } = questionCard;

    return {
      ...requiredColDef({ field: id, headerName: header }),
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => row[id],
      renderCell: ({ row }) => (
        <Cell>
          <TextCellContent Icon={PhoneIcon} value={row[id]} />
        </Cell>
      ),
    };
  };

const QuestionCardColumnHeader: FC<{ questionCard: QuestionCard }> = ({ questionCard }) => {
  const { header, questionText, helpInformation, extraHelpInformation } = questionCard;

  return (
    <ColumnHeader
      headerText={header}
      popoverBody={helpInformation}
      popoverHeader={questionText}
      popoverExtraHelpInformation={extraHelpInformation}
    />
  );
};

const getMultipleContactPersonInputColDef =
  () =>
  (questionCard: QuestionCard): GridColDefWithAnswerValue<"multipleContactPersonInput"> => {
    const { id, header } = questionCard;
    return {
      ...requiredColDef({ field: id, headerName: header }),
      width: 300,
      renderHeader: () => <QuestionCardColumnHeader questionCard={questionCard} />,
      valueGetter: (_, row) => {
        const value = row[id];
        if (!value) return null;
        return value
          .map(contactPerson => Object.values(contactPerson).join(Grid.VALUE_GETTER_DELIMITER))
          .join(Grid.VALUE_GETTER_DELIMITER);
      },
      renderCell: ({ row }) => {
        const value = row[id];
        if (!value) return <EmptyCell />;
        return (
          <Cell withExtraSpacingTop={false}>
            <LimitVisibleItems
              items={value}
              numberOfVisibleItems={1}
              renderItem={contactPerson => (
                <ContactPersonContent contactPerson={contactPerson} key={contactPerson.email} />
              )}
            />
          </Cell>
        );
      },
    };
  };

export const EmptyCell = () => <Box sx={{ opacity: 0.5, color: theme => theme.palette.text.tertiary }}>–</Box>;

export const LimitVisibleItems = <Item,>({
  items,
  numberOfVisibleItems,
  renderItem,
  stacked = false,
}: {
  items: Item[];
  numberOfVisibleItems: number;
  renderItem: (item: Item) => ReactNode;
  stacked?: boolean;
}) => {
  const { popoverProps, anchorProps } = useHoverPopover();

  const visibleItems = items.slice(0, numberOfVisibleItems);
  const hiddenItems = items.slice(numberOfVisibleItems);

  const hiddenItemsChip = hiddenItems.length > 0 && (
    <Stack
      onClick={e => e.stopPropagation()}
      {...anchorProps}
      sx={{
        alignSelf: "center",
        bgcolor: theme => theme.palette.background.paper,
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid",
        borderColor: theme => theme.palette.text.tertiary,
        borderRadius: "4px",
        fontSize: theme => theme.typography.pxToRem(12),
        fontWeight: "700",
        color: theme => theme.palette.text.tertiary,
        px: 1,
        py: 0,
      }}
    >
      <Box component="span" sx={{ transform: "translateY(0.5px)" }}>
        +{hiddenItems.length}
      </Box>
    </Stack>
  );

  return (
    <>
      {stacked ? (
        <Stack sx={{ rowGap: 0.5 }}>
          {visibleItems.slice(0, -1).map(renderItem)}
          <Stack direction="row" sx={{ columnGap: 1 }}>
            {visibleItems.slice(-1).map(renderItem)}
            {hiddenItemsChip}
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
          {visibleItems.map(renderItem)}
          {hiddenItemsChip}
        </Stack>
      )}
      <HoverPopover {...popoverProps}>
        <Stack direction={stacked ? "column" : "row"} sx={{ gap: 1, p: 3, flexWrap: "wrap", width: "30rem" }}>
          {hiddenItems.map(renderItem)}
        </Stack>
      </HoverPopover>
    </>
  );
};

export const TextCellContent = ({
  value,
  description,
  Icon,
}: {
  value?: string | null;
  description?: string;
  Icon?: ComponentType<IconProps>;
}) => {
  if (!value) return <EmptyCell />;
  return (
    <Stack direction="row" sx={{ alignItems: description ? "flex-start" : "center", gap: 1 }}>
      {Icon && (
        <Box sx={{ display: "flex", pt: description ? "2px" : 0 }}>
          <Icon size={16} color={theme => theme.palette.text.secondary} />
        </Box>
      )}
      <Stack sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            ...HACK_alignIconWithLabel,
            color: theme => theme.palette.text.secondary,
            fontSize: theme => theme.typography.pxToRem(14),
            ...multilineEllipsis(2),
          }}
          component="span"
        >
          {value}
        </Typography>
        {description && (
          <Typography
            sx={{
              color: theme => theme.palette.text.tertiary,
              fontSize: theme => theme.typography.pxToRem(12),
              ...multilineEllipsis(1),
            }}
            component="span"
          >
            {description}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export const Cell = ({
  children,
  withExtraSpacingTop = true,
}: {
  children: ReactNode;
  /** Should be false if the cell content has built-in spacing, the goal is to align the text within each cell. */
  withExtraSpacingTop?: boolean;
}) => {
  return <div style={{ marginTop: withExtraSpacingTop ? "3px" : 0 }}>{children}</div>;
};
